import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { OrderService } from "@/gen/omnios/v1/order_connect";
import { formatCurrency } from "@/lib/format";
import { transport } from "@/lib/grpc";
import { useEscapeBack } from "@/hooks/use-escape-back";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/commerce/returns/$id")({
	component: ReturnDetailPage,
});

const client = createClient(OrderService, transport);

function ReturnDetailPage() {
	const { t } = useTranslation("commerce");
	const { id } = Route.useParams();
	const qc = useQueryClient();
	useEscapeBack("/commerce/returns");

	const {
		data: ret,
		isLoading,
		error,
	} = useQuery({
		queryKey: ["return", id],
		queryFn: () => client.getReturn({ id }),
	});

	useBreadcrumbs([
		{ label: t("returns_title"), href: "/commerce/returns" },
		{ label: ret ? `#${String(ret.seqNo)}` : "..." },
	]);

	if (isLoading) {
		return (
			<p className="py-8 text-center text-sm text-muted-foreground">
				{t("common:loading", { defaultValue: "Загрузка..." })}
			</p>
		);
	}

	if (error || !ret) {
		return (
			<div className="py-16 text-center text-sm text-muted-foreground">
				{t("return_not_found", { defaultValue: "Возврат не найден" })}
			</div>
		);
	}

	const isPending =
		ret.statusDisplayName?.toLowerCase() === "pending" ||
		ret.statusDisplayName?.toLowerCase() === "requested";

	return (
		<div className="space-y-6 p-6">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0 justify-end">
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => qc.invalidateQueries({ queryKey: ["return", id] })}
				>
					{t("menu_refresh")}
				</Button>
			</div>
			<div className="flex items-center gap-3">
				<Link
					to="/commerce/returns"
					className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
				>
					<ArrowLeft className="h-4 w-4" />
				</Link>
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2">
						<span className="text-lg font-semibold">
							{t("return_detail")} #{String(ret.seqNo)}
						</span>
						{ret.statusDisplayName && (
							<Badge
								style={{
									backgroundColor: `color-mix(in srgb, ${ret.statusColor || "hsl(var(--muted-foreground))"} 12%, transparent)`,
									color: ret.statusColor || "hsl(var(--muted-foreground))",
									borderColor: `color-mix(in srgb, ${ret.statusColor || "hsl(var(--muted-foreground))"} 25%, transparent)`,
								}}
							>
								{ret.statusDisplayName}
							</Badge>
						)}
						<Badge variant="outline">
							{t(`resolution_${ret.resolution}`, {
								defaultValue: ret.resolution,
							})}
						</Badge>
					</div>
				</div>
				{isPending && <ApproveReturnDialog returnId={ret.id} />}
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				<div className="lg:col-span-2 space-y-4">
					{/* Return lines */}
					<Card>
						<CardHeader className="py-3">
							<CardTitle className="text-sm">{t("return_lines")}</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							{ret.lines.length === 0 ? (
								<p className="py-6 text-center text-sm text-muted-foreground">
									{t("no_returns")}
								</p>
							) : (
								<Table>
									<TableHeader>
										<TableRow className="bg-muted/30">
											<TableHead>#</TableHead>
											<TableHead>
												{t("col_product", { defaultValue: "Товар" })}
											</TableHead>
											<TableHead className="text-right w-20">
												{t("col_quantity", { defaultValue: "Кол-во" })}
											</TableHead>
											<TableHead>{t("return_reason")}</TableHead>
											<TableHead>
												{t("return_condition", {
													defaultValue: "Состояние",
												})}
											</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{ret.lines.map((line, i) => (
											<TableRow key={line.id}>
												<TableCell className="text-muted-foreground">
													{i + 1}
												</TableCell>
												<TableCell className="font-medium">
													{line.productId}
												</TableCell>
												<TableCell className="text-right tabular-nums">
													{line.quantity}
												</TableCell>
												<TableCell>{line.reason || "—"}</TableCell>
												<TableCell>
													<Badge variant="outline">
														{t(`condition_${line.condition}`, {
															defaultValue: line.condition,
														})}
													</Badge>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}
						</CardContent>
					</Card>
				</div>

				<aside className="space-y-4">
					<Card>
						<CardHeader className="py-3">
							<CardTitle className="text-sm">
								{t("return_info", { defaultValue: "Информация" })}
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-2 text-sm">
							<div className="flex justify-between">
								<span className="text-muted-foreground">
									{t("return_amount")}
								</span>
								<span className="font-medium tabular-nums">
									{formatCurrency(ret.refundAmountCents)}
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-muted-foreground">
									{t("return_resolution")}
								</span>
								<span>
									{t(`resolution_${ret.resolution}`, {
										defaultValue: ret.resolution,
									})}
								</span>
							</div>
							{ret.reason && (
								<div>
									<p className="text-muted-foreground mb-1">
										{t("return_reason")}
									</p>
									<p>{ret.reason}</p>
								</div>
							)}
							{ret.customerNote && (
								<div>
									<p className="text-muted-foreground mb-1">
										{t("customer_note", {
											defaultValue: "Комментарий клиента",
										})}
									</p>
									<p>{ret.customerNote}</p>
								</div>
							)}
							{ret.internalNote && (
								<div>
									<p className="text-muted-foreground mb-1">
										{t("internal_note")}
									</p>
									<p>{ret.internalNote}</p>
								</div>
							)}
							<div className="flex justify-between">
								<span className="text-muted-foreground">
									{t("date_col")}
								</span>
								<span>
									{ret.createdAt
										? new Date(
												Number(ret.createdAt.seconds) * 1000,
											).toLocaleDateString("ru-RU")
										: "—"}
								</span>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardContent className="py-3">
							<Link
								to="/commerce/orders/$id"
								params={{ id: ret.orderId }}
								className="text-sm text-primary hover:underline"
							>
								{t("return_back_to_order")}
							</Link>
						</CardContent>
					</Card>
				</aside>
			</div>
		</div>
	);
}

function ApproveReturnDialog({ returnId }: { returnId: string }) {
	const { t } = useTranslation("commerce");
	const qc = useQueryClient();
	const [open, setOpen] = useState(false);
	const [internalNote, setInternalNote] = useState("");
	const [refundAmount, setRefundAmount] = useState("");

	const approveMut = useMutation({
		mutationFn: () =>
			client.approveReturn({
				id: returnId,
				internalNote,
				refundAmountCents: refundAmount
					? BigInt(refundAmount)
					: BigInt(0),
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["return", returnId] });
			setOpen(false);
			setInternalNote("");
			setRefundAmount("");
		},
	});

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button size="sm">
					<Check className="h-4 w-4 mr-1" />
					{t("approve_return")}
				</Button>
			</DialogTrigger>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t("approve_return")}</DialogTitle>
				</DialogHeader>
				<div className="space-y-3">
					<div>
						<Label>
							{t("internal_note")}
						</Label>
						<Textarea
							value={internalNote}
							onChange={(e) => setInternalNote(e.target.value)}
							placeholder={t("internal_note")}
							rows={3}
							className="mt-1"
						/>
					</div>
					<div>
						<Label>
							{t("refund_amount")}
						</Label>
						<Input
							type="number"
							value={refundAmount}
							onChange={(e) => setRefundAmount(e.target.value)}
							placeholder="0"
							className="mt-1"
						/>
						<p className="text-xs text-muted-foreground mt-1">
							{t("refund_amount_hint", {
								defaultValue: "В копейках. 0 = полная сумма.",
							})}
						</p>
					</div>
				</div>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => setOpen(false)}
						disabled={approveMut.isPending}
					>
						{t("common:cancel", { defaultValue: "Отмена" })}
					</Button>
					<Button
						onClick={() => approveMut.mutate()}
						disabled={approveMut.isPending}
					>
						{approveMut.isPending && (
							<Loader2 className="h-4 w-4 mr-1 animate-spin" />
						)}
						{t("approve_return")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
